import * as Sentry from '@sentry/react';

interface ErrorContext {
  operation: string;
  action?: string;
  userId?: string;
  metadata?: Record<string, any>;
}

// Patterns that indicate user/validation errors that should NOT be reported
const USER_ERROR_PATTERNS = [
  /validation/i,
  /required field/i,
  /invalid format/i,
  /permission denied/i,
  /please enter/i,
  /please fill/i,
  /must be/i,
  /should be/i,
  /already exists/i,
  /duplicate/i,
  /not found/i,
  /unauthorized/i,
  /forbidden/i,
];

// Patterns that indicate system errors that SHOULD be reported
const SYSTEM_ERROR_PATTERNS = [
  /network/i,
  /timeout/i,
  /connection/i,
  /database/i,
  /internal server/i,
  /500/i,
  /502/i,
  /503/i,
  /504/i,
  /supabase/i,
  /fetch/i,
  /CORS/i,
  /socket/i,
];

/**
 * Determines if an error is a system error that should be reported to Sentry
 */
function isSystemError(error: any): boolean {
  if (!error) return false;
  
  const errorMessage = error.message || error.toString();
  
  // Check if it's explicitly a user error
  const isUserError = USER_ERROR_PATTERNS.some(pattern => 
    pattern.test(errorMessage)
  );
  
  if (isUserError) return false;
  
  // Check if it's explicitly a system error
  const isExplicitSystemError = SYSTEM_ERROR_PATTERNS.some(pattern => 
    pattern.test(errorMessage)
  );
  
  // Also check error codes
  const systemErrorCodes = ['NETWORK_ERROR', 'TIMEOUT', 'INTERNAL_ERROR', 'SERVER_ERROR'];
  const hasSystemErrorCode = error.code && systemErrorCodes.includes(error.code);
  
  // Check HTTP status codes
  const hasServerErrorStatus = error.status && error.status >= 500;
  
  return isExplicitSystemError || hasSystemErrorCode || hasServerErrorStatus;
}

/**
 * Sanitizes sensitive data from objects before sending to Sentry
 */
function sanitizeData(data: any): any {
  if (!data) return data;
  
  const sensitiveKeys = ['password', 'token', 'secret', 'key', 'auth', 'photo_name', 'email'];
  
  if (typeof data === 'object') {
    const sanitized = { ...data };
    
    Object.keys(sanitized).forEach(key => {
      const lowerKey = key.toLowerCase();
      if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof sanitized[key] === 'object') {
        sanitized[key] = sanitizeData(sanitized[key]);
      }
    });
    
    return sanitized;
  }
  
  return data;
}

/**
 * Captures system errors with context to Sentry
 * Only reports actual system/infrastructure errors, not user errors
 */
export function captureSystemError(
  error: any, 
  context: ErrorContext
): void {
  // Only capture if it's a system error
  if (!isSystemError(error)) {
    console.error(`User error in ${context.operation}:`, error);
    return;
  }
  
  // Prepare sanitized metadata
  const sanitizedMetadata = sanitizeData(context.metadata);
  
  // Capture to Sentry with context
  Sentry.captureException(error, {
    tags: {
      feature: context.operation,
      action: context.action || 'unknown',
      error_type: 'system_error',
    },
    contexts: {
      operation: {
        type: context.operation,
        action: context.action,
        user_id: context.userId,
      },
    },
    extra: {
      metadata: sanitizedMetadata,
      error_code: error.code,
      error_hint: error.hint,
      error_details: error.details,
      original_message: error.message,
    },
    level: 'error',
  });
  
  // Also log to console for development
  console.error(`System error in ${context.operation}:`, {
    error,
    context,
    metadata: sanitizedMetadata,
  });
}

/**
 * Wraps an async function with system error reporting
 */
export function withSystemErrorReporting<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context: Omit<ErrorContext, 'metadata'>
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      captureSystemError(error, {
        ...context,
        metadata: { args: sanitizeData(args) },
      });
      throw error;
    }
  }) as T;
}